import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { MapPinIcon, BriefcaseIcon, UsersIcon, CubeIcon, CurrencyDollarIcon } from "@heroicons/react/24/outline";
import Navbar from "../constants/Navbar";
import { formatField } from "../utils/universalFormatter";
import { SkeletonLoader } from "../components/common/SkeletonLoader";
import { useAlert } from "../constants/AlertProvider";

export default function JobShare() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const showAlert = useAlert();

    useEffect(() => {
        const fetchJobs = async () => {
            setIsLoading(true);
            try {
                const jobRes = await axios.get(`/api/jobprofile/${id}/`, { withCredentials: true });
                const jobData = jobRes.data;

                // Fetch company info for the job
                const companyRes = await axios.get(`/api/company/${jobData.company}/`, { withCredentials: true });
                const jobWithCompany = { ...jobData, companyData: companyRes.data };
                setJob(jobWithCompany);
            } catch (error) {
                showAlert("Error fetching job", "error");
                console.error("Error fetching job:", error);
                setJob(null);
            } finally {
                setIsLoading(false);
            }
        };
        fetchJobs();
    }, [id]);

    const formatKeywords = (keywords) => {
        if (!keywords) return "";
        const keywordList = keywords.split(',').map(k => k.trim());
        if (keywordList.length > 3) {
            return `${keywordList.slice(0, 3).join(', ')}...`;
        }
        return keywords;
    };

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col pb-28">
                {isLoading ? (
                    <div className="w-full">
                        <SkeletonLoader />
                    </div>
                ) : !job || !job.is_active ? (
                    <div className="w-full flex-grow flex items-center justify-center">
                        <div className="w-full max-w-3xl p-10 text-center">
                            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Job not found</h2>
                            <p className="text-gray-500 dark:text-gray-400 mt-2 mb-6">
                                The job you are looking for does not exist or has been removed.
                            </p>
                            <button
                                onClick={() => navigate("/landingpage")}
                                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg shadow hover:bg-blue-700 transition-transform transform hover:scale-105"
                            >
                                Signup to Get Matched
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="w-full flex-grow flex flex-col">
                        {/* Header Section */}
                        <div className="w-full bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-6 flex items-center gap-4">
                            <img
                                src={job.companyData.logo}
                                alt="logo"
                                className="w-14 h-14 rounded-md border border-gray-200"
                            />
                            <div>
                                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{job.title}</h1>
                                <p className="text-gray-600 dark:text-gray-400 text-sm">{job.companyData.name}</p>
                            </div>
                        </div>

                        {/* Meta Info */}
                        <div className="w-full bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                            <div className="grid grid-cols-3 gap-4 text-gray-700 dark:text-gray-300 text-sm mb-2">
                                <div className="flex items-center gap-2">
                                    <BriefcaseIcon className="w-4 h-4 text-gray-400" />
                                    <span>{formatField("job_type", job.job_type)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CubeIcon className="w-4 h-4 text-gray-400" />
                                    <span>{formatKeywords(job.keywords)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CurrencyDollarIcon className="w-4 h-4 text-gray-400" />
                                    <span>
                                        ${formatField("salary", job.salary_min)}K - $
                                        {formatField("salary", job.salary_max)}K
                                    </span>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-gray-700 dark:text-gray-300 text-sm">
                                <div className="flex items-center gap-2">
                                    <MapPinIcon className="w-4 h-4 text-gray-400" />
                                    <span>
                                        {job.city}, {job.state}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <BriefcaseIcon className="w-4 h-4 text-gray-400" />
                                    <span>{job.years_of_experience} yrs exp</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <UsersIcon className="w-4 h-4 text-gray-400" />
                                    <span>
                                        {formatField("employee_count", job.companyData.employee_count)}{" "}
                                        employees
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        <div className="px-6 py-8 bg-white dark:bg-gray-800">
                            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
                                Job Description
                            </h2>
                            <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-sm">
                                {job.description}
                            </p>
                        </div>

                    </div>
                )}
                {/* Sticky CTA Footer */}
                {!isLoading && job && job.is_active && (
                    <div className="fixed bottom-6 right-6">
                        <button onClick={() => navigate("/landingpage")} className="px-6 py-3 bg-blue-600 text-white text-sm font-medium rounded-lg shadow-lg hover:bg-blue-700 transition-transform transform hover:scale-105">
                            Signup to Get Matched
                        </button>
                    </div>
                )}
            </div>
        </>
    );

};
